import { cn } from "@/lib/utils";
import { createContext, useContext, type ComponentProps } from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import Button, { IconButton } from "../button/button";

export const DrawerContext = createContext(
  {} as {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
  },
);

export function useDrawer() {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error("useDrawer must be used within a Drawer");
  }
  return context;
}

export default function Drawer({
  open,
  onOpenChange,
  ...props
}: ComponentProps<typeof DrawerPrimitive.Root>) {
  return (
    <DrawerContext.Provider value={{ open, onOpenChange }}>
      <DrawerPrimitive.Root
        data-slot="drawer"
        open={open}
        onOpenChange={onOpenChange}
        {...props}
      />
    </DrawerContext.Provider>
  );
}

export function DrawerTrigger({
  ...props
}: ComponentProps<typeof DrawerPrimitive.Trigger>) {
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}

export function DrawerPortal({
  ...props
}: ComponentProps<typeof DrawerPrimitive.Portal>) {
  return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />;
}

export function DrawerClose(props: ComponentProps<typeof IconButton>) {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />;
}

export function DrawerOverlay({
  className,
  ...props
}: ComponentProps<typeof DrawerPrimitive.Overlay>) {
  return (
    <DrawerPrimitive.Overlay
      data-slot="drawer-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

export function DrawerContent({
  width,
  children,
  className,
  classNames,
  overlayProps,
  ...props
}: ComponentProps<typeof DrawerPrimitive.Content> & {
  /** Don't use this prop, only for compatibility with DialogContent. */
  width?: string;
  classNames?: {
    root?: string;
    content?: string;
  };
  overlayProps?: ComponentProps<typeof DrawerPrimitive.Overlay>;
}) {
  return (
    <DrawerPortal>
      <DrawerOverlay {...overlayProps} />
      <DrawerPrimitive.Content
        data-slot="drawer-content"
        className={cn(
          "group/drawer-content bg-background fixed z-50 flex max-h-[80vh] flex-col",
          "data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[80vh] data-[vaul-drawer-direction=top]:rounded-b-lg data-[vaul-drawer-direction=top]:border-b",
          "data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[80vh] data-[vaul-drawer-direction=bottom]:rounded-t-lg data-[vaul-drawer-direction=bottom]:border-t",
          "data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=right]:border-l data-[vaul-drawer-direction=right]:sm:max-w-sm",
          "data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=left]:border-r data-[vaul-drawer-direction=left]:sm:max-w-sm",
          width,
          className,
          classNames?.root,
        )}
        {...props}
      >
        <div
          className={cn(
            "relative isolate flex flex-1 flex-col gap-6 overflow-y-auto px-4 pt-12 pb-6",
            classNames?.content,
          )}
        >
          {/* The handle for the drawer */}
          <div className="bg-background-neutral-disabled absolute top-5 left-1/2 mx-auto hidden h-1.5 w-12 shrink-0 -translate-x-1/2 rounded-full group-data-[vaul-drawer-direction=bottom]/drawer-content:block" />
          {children}
        </div>
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
}

export function DrawerHeader({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-header"
      className={cn("flex flex-col gap-2 text-center", className)}
      {...props}
    />
  );
}

export function DrawerFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn("flex items-center justify-stretch gap-2 pt-2", className)}
      {...props}
    />
  );
}

export function DrawerTitle({
  className,
  ...props
}: ComponentProps<typeof DrawerPrimitive.Title>) {
  return (
    <DrawerPrimitive.Title
      data-slot="drawer-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

export function DrawerDescription({
  className,
  ...props
}: ComponentProps<typeof DrawerPrimitive.Description>) {
  return (
    <DrawerPrimitive.Description
      data-slot="drawer-description"
      className={cn("text-foreground-pressed text-sm", className)}
      {...props}
    />
  );
}

export function DrawerAction({
  className,
  ...props
}: React.ComponentProps<typeof Button>) {
  return <Button className={cn("flex-1", className)} {...props} />;
}

export function DrawerCancel({
  className,
  ...props
}: React.ComponentProps<typeof Button>) {
  return (
    <Button
      variant="secondary"
      className={cn("flex-1", className)}
      {...props}
    />
  );
}
